import type { ChatUIAction } from '@/lib/api/endpoints/chat'
import type { ArtifactContent, ArtifactStatus } from '@xavior/shared/studio'

export interface ActiveArtifactEntry {
  content: ArtifactContent
  themeId: string
  brandKitId?: string
  moduleId?: string
  /** The persisted objects(type='artifact') row id, set from the artifact.saved
   *  directive (PR-4). Threaded into ArtifactCardData so the draft card's
   *  Send/Publish target the real row, not an ephemeral draft. */
  objectId?: string
  /** The artifact lifecycle status, set from the terminal artifact.saved re-emit's
   *  `status` field (PR-5b). Threaded into ArtifactCardData so the draft card gates
   *  Send/Publish on status==='ready'. Undefined while still building. */
  status?: ArtifactStatus
  firstBlockFired?: boolean
}

/**
 * Build one `result.card` action (kind: 'artifact') per artifact drafted this
 * turn. `existingActions` lets the caller pass agent-emitted ui-actions so a
 * future agent-authored artifact card for the same content isn't doubled.
 * Mirrors `deriveReportResultCardActions` in report-result-card.ts.
 */
export function deriveArtifactCardActions(
  activeArtifacts: ReadonlyMap<string, ActiveArtifactEntry>,
  existingActions: ReadonlyArray<ChatUIAction | null | undefined> = [],
): ChatUIAction[] {
  const alreadyCarded = new Set<string>()
  for (const action of existingActions) {
    if (!action || (action as { type?: string }).type !== 'result.card') continue
    const card = (action as { card?: { kind?: unknown; data?: { artifactId?: unknown } } }).card
    if (card?.kind === 'artifact' && typeof card.data?.artifactId === 'string') {
      alreadyCarded.add(card.data.artifactId)
    }
  }

  const actions: ChatUIAction[] = []
  for (const [artifactId, entry] of activeArtifacts) {
    if (!entry.content?.pages?.some((p) => (p.blocks?.length ?? 0) > 0)) continue
    if (alreadyCarded.has(artifactId)) continue
    const data = {
      artifactId,
      content: entry.content,
      themeId: entry.themeId,
      ...(entry.brandKitId ? { brandKitId: entry.brandKitId } : {}),
      ...(entry.moduleId ? { moduleId: entry.moduleId } : {}),
      ...(entry.objectId ? { objectId: entry.objectId } : {}),
      ...(entry.status ? { status: entry.status } : {}),
    }
    actions.push({
      type: 'result.card',
      card: { kind: 'artifact', data: data as unknown as Record<string, unknown> },
    } as ChatUIAction)
  }
  return actions
}
